// Product analytics — the funnel instrumentation the app has never had.
//
// WHY THIS EXISTS
// Every strategy call to date has been made at n=1, because nothing records where
// coaches actually fall out. Two funnels matter:
//
//   Activation   team_created → roster_added → game_logged → stats_imported
//                → insights_generated
//                Deliberately mirrors the stages nightlyUsageRollup already counts
//                from Firestore, so in-app behaviour and adminMetrics/usageSnapshot
//                can be reconciled rather than argued about.
//
//   Trial        elite_prompt_shown → start_trial_tapped → checkout_opened
//                → checkout_completed
//                The drop between the last two IS the credit-card wall, measured.
//                That number decides whether the fall trial stays card-required.
//
// TWO TRANSPORTS, ONE API
// The Firebase JS Analytics SDK depends on browser storage and a measurement
// endpoint that behave unreliably inside the Capacitor WKWebView, so native does
// NOT use it. Native routes through AnalyticsPlugin.swift to the native Firebase
// SDK instead, which is already configured (AppDelegate calls FirebaseApp.configure
// for Cloud Messaging). Callers never branch: track() picks the transport. Same
// isNativePlatform() rule the rest of the shared code follows.
//
// The App target links FirebaseAnalyticsWithoutAdIdSupport, never plain
// FirebaseAnalytics: the default product collects the advertising identifier,
// which would force an ATT prompt and make the "Not used for tracking" App Store
// declaration false.
import { Capacitor, registerPlugin } from '@capacitor/core';
import { getAnalytics, isSupported, logEvent, setUserProperties, type Analytics } from 'firebase/analytics';
import { app } from '../firebase';

interface AnalyticsPluginDef {
  logEvent(o: { name: string; params?: Record<string, unknown> }): Promise<void>;
  setUserProperties(o: { props: Record<string, string> }): Promise<void>;
  setEnabled(o: { enabled: boolean }): Promise<void>;
}
// registerPlugin, not window.Capacitor.Plugins — app-embedded Swift plugins are
// exported by registerPluginInstance and reached this way.
const Native = registerPlugin<AnalyticsPluginDef>('AnalyticsPlugin');
const IS_NATIVE = Capacitor.isNativePlatform();

/** Every event this app emits. A closed union so a typo is a compile error
 *  rather than a silently-missing funnel stage six weeks from now. */
export type DugoutEvent =
  // ── Activation ──
  | 'team_created'
  | 'roster_added'
  | 'game_logged'
  | 'stats_imported'
  | 'insights_generated'
  // ── Trial / revenue ──
  | 'elite_prompt_shown'
  | 'start_trial_tapped'
  | 'checkout_opened'
  | 'checkout_completed'
  // ── Landing page / campaign ──
  // Everything above fires only AFTER a coach has an account, so until these
  // existed the top of the funnel was invisible: we could run a campaign and
  // never learn whether the offer was seen, ignored, or did the work.
  // `signup_opened` is fired from openCreateAccount() itself rather than from any
  // one button, so every CTA on the page shares a denominator and the banner's
  // contribution can be compared against the rest.
  | 'landing_viewed'
  | 'campaign_banner_clicked'
  | 'campaign_story_viewed'
  | 'signup_opened';

let analytics: Analytics | null = null;
let initStarted = false;
/** Events fired before init resolves are held here, not dropped — the earliest
 *  ones (onboarding) are exactly the ones the activation funnel needs most. */
const pending: { event: DugoutEvent; params: Record<string, unknown> }[] = [];
/** Demo sessions are screenshot scaffolding. Counting them would flatter every
 *  number, the same reason nightlyUsageRollup filters them out. */
let suppressed = false;

export async function initAnalytics(): Promise<void> {
  if (initStarted) return;
  initStarted = true;
  // Native needs no init: FirebaseApp.configure() already ran in AppDelegate, so
  // the plugin is callable immediately. Just drain anything queued during startup.
  if (IS_NATIVE) {
    flushPending();
    return;
  }
  try {
    if (!(await isSupported())) return;
    analytics = getAnalytics(app);
    flushPending();
  } catch {
    // Analytics is never allowed to break the app. A blocked measurement
    // endpoint (ad blockers catch this one routinely) must be invisible.
    analytics = null;
    pending.length = 0;
  }
}

function flushPending(): void {
  const queued = pending.splice(0, pending.length);
  for (const q of queued) track(q.event, q.params);
}

/** Call when a demo session starts, so its events never reach the funnel. */
export function suppressAnalytics(): void {
  suppressed = true;
  pending.length = 0;
  // Native collection is a process-level switch, so it has to be turned off in
  // the SDK too, not just guarded on this side of the bridge.
  if (IS_NATIVE) Native.setEnabled({ enabled: false }).catch(() => {});
}

export function track(event: DugoutEvent, params: Record<string, unknown> = {}): void {
  if (suppressed) return;
  try {
    if (IS_NATIVE) {
      Native.logEvent({ name: event, params }).catch(() => {});
      return;
    }
    if (!analytics) {
      if (pending.length < 50) pending.push({ event, params });
      return;
    }
    logEvent(analytics, event, params);
  } catch { /* never break a user flow for a metric */ }
}

/**
 * Dimensions every funnel gets sliced by. These mirror the Loops contact
 * properties and the rollup's `mix` buckets so the three data sources describe
 * the same populations.
 *
 * Never pass anything identifying — no email, no name, no team name. GA4 is
 * declared to Apple as "Not used for tracking", and that claim has to stay true.
 */
export function setAnalyticsUser(props: {
  styleOfPlay?: string;
  ageGroup?: string;
  org?: string;
  isHeadCoach?: boolean;
  isElite?: boolean;
}): void {
  if (suppressed) return;
  const dims = {
    style_of_play: props.styleOfPlay ?? '',
    age_group: props.ageGroup ?? '',
    org: props.org ?? '',
    is_head_coach: String(props.isHeadCoach ?? ''),
    is_elite: String(props.isElite ?? ''),
  };
  try {
    if (IS_NATIVE) {
      Native.setUserProperties({ props: dims }).catch(() => {});
      return;
    }
    if (!analytics) return;
    setUserProperties(analytics, dims);
  } catch { /* non-fatal */ }
}

// ── Checkout completion ──────────────────────────────────────────────────────
// Stripe Checkout leaves the app entirely and returns to success_url, so there is
// no in-page callback to hang `checkout_completed` on. Stash a marker before the
// redirect and settle it on return. sessionStorage (not local) so an abandoned
// checkout dies with the tab instead of mis-attributing a purchase days later.
const PENDING_KEY = 'dugout_checkout_pending';

export function markCheckoutStarted(plan: string): void {
  try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ plan, at: Date.now() })); } catch { /* private mode */ }
}

/**
 * Resolve a checkout that left the page. Call once entitlement is known.
 * `isElite` true after a redirect means Stripe accepted the card.
 */
export function resolvePendingCheckout(isElite: boolean): void {
  let raw: string | null = null;
  try { raw = sessionStorage.getItem(PENDING_KEY); } catch { return; }
  if (!raw) return;
  // Only settle once entitlement actually flips; the subscription doc can take a
  // moment to arrive, and clearing early would lose the conversion.
  if (!isElite) return;
  try { sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
  let plan = '';
  try { plan = JSON.parse(raw).plan ?? ''; } catch { /* ignore */ }
  track('checkout_completed', { plan });
}
